import HubOutlined from "@mui/icons-material/HubOutlined";
import { Box, type Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemTabs } from "../../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/SystemTabs";
import type { SystemTab } from "../../../../../components/UIToolkit/Clickable/TabsAndChips/SystemTabs/utils";
import { SettingsSection } from "../../../../../components/UIToolkit/molecules/SettingsSection";
import { SIDE_LAID_GROUND_SX } from "../ratesConsts";
import { EarningCodeIgnoreGrid } from "./EarningCodeIgnoreGrid";
import { EmployeeStatusMappingGrid } from "./EmployeeStatusMappingGrid";
import { EmploymentTypeMappingGrid } from "./EmploymentTypeMappingGrid";
import { PunchEventTypeIgnoreGrid } from "./PunchEventTypeIgnoreGrid";
import { RolesTabContent } from "./RolesTabContent";
import { SubRoleMappingGrid } from "./SubRoleMappingGrid";
import { TimeOffTabContent } from "./TimeOffTabContent";
import { HrisMappingView } from "./utils";

// Narrow the broad `SxProps` union to its callback shape so it can compose with this tab's extra spacing.
const groundStyle = SIDE_LAID_GROUND_SX as (theme: Theme) => SystemStyleObject<Theme>;

export const HRISMappingTabContent: FC = () => {
	const { t } = useTranslation();
	const [currentView, setCurrentView] = useState<HrisMappingView>(HrisMappingView.Roles);

	const VIEWS: SystemTab<HrisMappingView>[] = [
		{ label: t("hris.mapping.view.roles"), value: HrisMappingView.Roles },
		{ label: t("hris.mapping.view.employment"), value: HrisMappingView.Employment },
		{ label: t("hris.mapping.view.status"), value: HrisMappingView.Status },
		{ label: t("hris.mapping.view.timeOff"), value: HrisMappingView.TimeOff },
		{ label: t("hris.mapping.view.punchEventType"), value: HrisMappingView.PunchEventType },
		{ label: t("hris.mapping.view.earningCode"), value: HrisMappingView.EarningCode },
		{ label: t("hris.mapping.view.subRole"), value: HrisMappingView.SubRole },
	];

	// Roles & Jobs and Time Off both use the wide two-pane Pretty-Mapping layout; the rest are single grids.
	const isWideView = currentView === HrisMappingView.Roles || currentView === HrisMappingView.TimeOff;

	return (
		<Box sx={(theme) => ({ ...groundStyle(theme), gap: 2, py: { xs: 3, md: 4 } })} data-testid="hris-mapping-tab">
			<SettingsSection
				headerPlacement="side"
				icon={<HubOutlined />}
				title={t("hris.mapping.section.title")}
				description={t("hris.mapping.section.description")}
				layout="stack"
				contentMaxWidth="none"
				dataTestId="hris-mapping-section"
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
					<SystemTabs<HrisMappingView> tabs={VIEWS} currentTab={currentView} onTabChange={setCurrentView} dataTestId="hris-mapping-view-tabs" />
					{/* Wide 2-pane views keep the viewport-derived height; single-grid tabs size to content so a sparse grid reads as intentional. */}
					<Box
						sx={
							isWideView
								? { display: "flex", flexDirection: "column", width: "100%", height: "calc(100dvh - 340px)", minHeight: 320, gap: 1 }
								: { display: "flex", flexDirection: "column", width: { xs: "85%", xl: "70%" }, gap: 1 }
						}
					>
						{currentView === HrisMappingView.Roles && <RolesTabContent />}
						{currentView === HrisMappingView.Employment && <EmploymentTypeMappingGrid />}
						{currentView === HrisMappingView.Status && <EmployeeStatusMappingGrid />}
						{currentView === HrisMappingView.TimeOff && <TimeOffTabContent />}
						{currentView === HrisMappingView.PunchEventType && <PunchEventTypeIgnoreGrid />}
						{currentView === HrisMappingView.EarningCode && <EarningCodeIgnoreGrid />}
						{currentView === HrisMappingView.SubRole && <SubRoleMappingGrid />}
					</Box>
				</Box>
			</SettingsSection>
		</Box>
	);
};
